/**
 * Marque blanche (TF-436) : nom, nuances et logo que l'interface affiche,
 * rendus par `GET /api/marque` — route publique, la page de connexion s'en
 * habille. Les nuances se déduisent côté API ; ce module ne fait que les poser
 * en variables CSS (`--codialis-<nuance>`, composantes « r g b ») que lisent
 * les classes Tailwind de la charte.
 */

import type { CSSProperties } from 'react';

export interface NuancesMarque {
  navy: string;
  deep: string;
  blue: string;
  sky: string;
  mint: string;
  teal: string;
  accentTexte: string;
  accentPale: string;
  /** `sky` et `teal` lisibles en texte, appliqués sous « Contraste renforcé » (TF-341). */
  skyTexte: string;
  tealTexte: string;
}

export interface MarquePublique {
  nom: string;
  nuances: NuancesMarque;
  /** `null` : le logo de la charte, livré avec l'interface. */
  logo: { url: string } | null;
}

/** Clé de la marque publique dans le cache des requêtes. */
export const CLE_MARQUE = ['marque'];

/**
 * Nom affiché avant la première réponse de l'API, celui de `index.html` : les
 * couleurs de ce premier instant sont celles de `:root` (`index.css`).
 */
export const NOM_AVANT_CHARGEMENT = 'TOP FORMATION';

/** `accentTexte` → `--codialis-accent-texte`. */
function variable(cle: string): string {
  return `--codialis-${cle.replace(/[A-Z]/g, (lettre) => `-${lettre.toLowerCase()}`)}`;
}

/** `#0B2A5E` → `11 42 94` ; `null` pour ce qui n'est pas une couleur `#RRGGBB`. */
function composantes(hex: string): string | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const n = Number.parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/** Les variables CSS des nuances, prêtes pour un attribut `style` (aperçu dans un cadre). */
export function styleDesNuances(nuances: NuancesMarque): CSSProperties {
  const style: Record<string, string> = {};
  for (const [cle, hex] of Object.entries(nuances)) {
    const valeur = composantes(hex);
    if (valeur) style[variable(cle)] = valeur;
  }
  return style as CSSProperties;
}

/** Pose les nuances sur un élément — `<html>` pour toute l'interface. */
export function poserNuances(element: HTMLElement, nuances: NuancesMarque): void {
  for (const [cle, valeur] of Object.entries(styleDesNuances(nuances))) {
    element.style.setProperty(cle, String(valeur));
  }
}

/**
 * Titre de l'onglet (RGAA 8.6, TF-341) : « Apprenants · TOP FORMATION » sur un
 * écran, « TOP FORMATION · Plateforme de gestion » sans écran.
 */
export function titreDocument(nom: string, ecran: string | null = null): string {
  return ecran ? `${ecran} · ${nom}` : `${nom} · Plateforme de gestion`;
}

// La marque et l'écran se posent chacun de leur côté, à des moments
// différents : le titre est recomposé avec la dernière valeur de l'autre.
let nomDuTitre = NOM_AVANT_CHARGEMENT;
let ecranDuTitre: string | null = null;

export function poserTitreDocument(changement: { nom?: string; ecran?: string | null }): void {
  if (changement.nom !== undefined) nomDuTitre = changement.nom;
  if (changement.ecran !== undefined) ecranDuTitre = changement.ecran;
  document.title = titreDocument(nomDuTitre, ecranDuTitre);
}

const CLE_STOCKAGE = 'tf.marque';

/**
 * La dernière marque reçue, gardée dans le navigateur : posée avant le premier
 * rendu, elle évite d'afficher un instant la charte d'origine. Un stockage
 * indisponible (navigation privée) ne fait que retarder la marque.
 */
export function marqueMemorisee(): MarquePublique | null {
  try {
    const brut = window.localStorage.getItem(CLE_STOCKAGE);
    if (!brut) return null;
    const lue = JSON.parse(brut) as Partial<MarquePublique>;
    return typeof lue.nom === 'string' && lue.nuances && typeof lue.nuances === 'object'
      ? { nom: lue.nom, nuances: lue.nuances as NuancesMarque, logo: lue.logo ?? null }
      : null;
  } catch {
    return null;
  }
}

export function memoriserMarque(marque: MarquePublique): void {
  try {
    window.localStorage.setItem(CLE_STOCKAGE, JSON.stringify(marque));
  } catch {
    // Stockage refusé : la marque sera reposée à la prochaine réponse de l'API.
  }
}

/** Au démarrage, avant le rendu : la marque connue de ce navigateur. */
export function amorcerMarque(): void {
  const memorisee = marqueMemorisee();
  if (!memorisee) return;
  poserNuances(document.documentElement, memorisee.nuances);
  poserTitreDocument({ nom: memorisee.nom });
}

/** « TOP FORMATION » → « TF » : la vignette du menu. */
export function initialesDuNom(nom: string): string {
  const tous = nom.split(/[\s'’-]+/).filter((mot) => /\p{L}|\d/u.test(mot));
  const longs = tous.filter((mot) => mot.length > 1);
  const mots = longs.length > 0 ? longs : tous;
  const lettres = mots.length > 1 ? [mots[0]![0], mots[1]![0]] : [...(mots[0] ?? nom).slice(0, 2)];
  return lettres.join('').toLocaleUpperCase('fr-FR');
}
